import React, { useState, useEffect } from 'react'
import { BsSearch, BsX } from 'react-icons/bs'
import { useDebouncedState } from 'hooks/use-debounced-state'
import { useInfiniteScroll } from 'pages/Planning/hooks/useInfiniteScroll'

export interface IContestOption {
  id: string
  name: string
  role: string
  situation: string
  career: string
  icon: string
  isSelected: boolean
}

export interface IContestSelectionProps {
  options: IContestOption[]
  selectedId: string | null
  onSelect: (id: string) => void
  onNext: (stepId: string) => void
  loading?: boolean
  searchValue?: string
  onSearch?: (searchTerm: string) => void
  loadMore?: () => void
}

const ContestSelection: React.FC<IContestSelectionProps> = ({
  options,
  selectedId,
  onSelect,
  onNext,
  loading = false,
  searchValue = '',
  onSearch,
  loadMore,
}) => {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue)
  const [debouncedSearchValue, setDebouncedSearchValue] = useDebouncedState(searchValue, 500)
  const [isSearching, setIsSearching] = useState(false)
  const scrollRef = useInfiniteScroll(loadMore)

  // Sync local search value with prop
  useEffect(() => {
    setLocalSearchValue(searchValue)
  }, [searchValue])

  // Trigger search when debounced value changes
  useEffect(() => {
    if (onSearch && debouncedSearchValue !== searchValue) {
      setIsSearching(true)
      onSearch(debouncedSearchValue)
      // Reset searching state after a short delay
      const timer = setTimeout(() => setIsSearching(false), 100)
      return () => clearTimeout(timer)
    }
  }, [debouncedSearchValue, onSearch, searchValue])

  const ContestSkeleton = ({ idx }: { idx: number }) => (
    <div key={`number-slide-${idx}`} className={`contest-option skeleton number-slide${idx}`}>
      <div className="contest-option-header">
        <div className="contest-option-radio loading-block skeleton-radio" />
        <div className="contest-option-icon loading-block skeleton-icon" />
      </div>

      <div className="contest-option-text">
        <div className="loading-block skeleton-title" />
        <div className="loading-block skeleton-subtitle" />
        <div className="loading-block skeleton-description" />
        <div className="loading-block skeleton-description-2" />
      </div>

      <div className="contest-option-footer">
        <div className="loading-block skeleton-career" />
      </div>
    </div>
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalSearchValue(value)
    setDebouncedSearchValue(value)
  }

  const handleClearSearch = () => {
    setLocalSearchValue('')
    setDebouncedSearchValue('')
  }

  const getSituationColor = (situation: string) => {
    if (['Previsto', 'Anunciado'].includes(situation)) {
      return 'text-rose-600 bg-rose-100'
    }

    if (['Banca Definida', 'Comissão Formada'].includes(situation)) {
      return 'text-sky-600 bg-sky-100'
    }

    if (['Edital a qualquer momento'].includes(situation)) {
      return 'text-amber-600 bg-amber-100'
    }

    if (['Autorizado', 'Edital Publicado'].includes(situation)) {
      return 'text-emerald-600 bg-emerald-100'
    }
  }

  return (
    <div className="contest-selection">
      <p className="contest-description">
        Selecione o concurso público para o qual você está se preparando:
      </p>

      <div className="contest-search-container-wrapper">
        {onSearch && (
          <div className="contest-search-container" data-tour="contest-search">
            <div className="contest-search-input-wrapper">
              <BsSearch
                className={`contest-search-icon ${isSearching ? 'searching' : ''}`}
                size={18}
              />
              <input
                type="text"
                className="contest-search-input"
                placeholder="Buscar concursos..."
                value={localSearchValue}
                onChange={handleSearchChange}
                disabled={loading}
              />
              {localSearchValue && (
                <button
                  type="button"
                  className="contest-search-clear"
                  onClick={handleClearSearch}
                  disabled={loading}
                  aria-label="Limpar busca"
                >
                  <BsX size={18} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="contest-options" ref={scrollRef} data-tour="contest-grid">
        {loading && !options.length ? (
          // Show 5 skeleton items when loading
          Array.from({ length: 8 }, (_, idx) => (
            <ContestSkeleton idx={idx} key={`number-slide-${idx}`} />
          ))
        ) : options.length === 0 && localSearchValue ? (
          // Show no results message when search returns empty
          <div className="contest-no-results">
            <p>Nenhum concurso encontrado para "{localSearchValue}"</p>
            <button type="button" className="contest-clear-search-btn" onClick={handleClearSearch}>
              Limpar busca
            </button>
          </div>
        ) : (
          <>
            {options.map((option, idx) => (
              <div
                key={option.id}
                className={`contest-option ${selectedId === option.id ? 'selected' : ''}`}
                onClick={() => onSelect(option.id)}
                role="button"
                tabIndex={0}
                data-tour={idx === 0 || selectedId === option.id ? 'contest-card' : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(option.id)
                  }
                }}
              >
                <div className="contest-option-header">
                  <div
                    className={`contest-option-radio ${selectedId === option.id ? 'selected' : ''}`}
                  />
                  <div className="contest-option-icon">
                    {option.icon ? (
                      <img src={option.icon} alt={option.name} />
                    ) : (
                      <div className="contest-option-fallback-icon">
                        {option.name
                          .trim()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .replace(/-/g, '')
                          .split(' ')
                          .filter((p) => p.length > 2)
                          .map((p) => p[0].toUpperCase())
                          .join('')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="contest-option-text">
                  <h4 className="contest-option-name">{option.name}</h4>
                  <p className="contest-option-description">{option.role}</p>
                </div>

                <div className="contest-option-footer">
                  <span
                    className={`contest-option-footer-situation ${getSituationColor(
                      option.situation
                    )}`}
                  >
                    {option.situation}
                  </span>
                </div>
              </div>
            ))}
            {loading && options.length
              ? Array.from({ length: 4 }, (_, idx) => (
                  <ContestSkeleton idx={idx} key={`number-slide-${idx}`} />
                ))
              : null}
          </>
        )}
      </div>

      <div className="item-next-button">
        <button
          className="next-button"
          data-tour="wizard-next"
          onClick={() => onNext('step-2')}
          disabled={!selectedId || loading}
        >
          Próximo
        </button>
      </div>
    </div>
  )
}

export default ContestSelection
